import React, { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "../context/DataContext";
import { OverviewCalendar } from "../components/OverviewCalendar";
import { Reservation } from "../types";
import "./PersonalPage.css";

// Simple SVG Icons for a clean, dependency-free look
const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/>
  </svg>
);
const DeskIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
        <path d="M14 3a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-2.5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1H14zM2 3a1 1 0 0 1 1-1h2.5a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4zm4.5 0a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v8a1 1 0 0 1-1-1h-1a1 1 0 0 1-1-1V4z"/>
    </svg>
);

export const PersonalPage = () => {
  const { reservations, desks, loading, user } = useData();
  const navigate = useNavigate();
  const [highlightedDate, setHighlightedDate] = useState<string | null>(null);

  const reservationRefs = useRef(new Map<string, HTMLElement>());

  const sortedReservations = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to the beginning of the day for accurate comparison
    const todayString = today.toISOString().split('T')[0];

    return [...reservations]
        .filter(r => r.date >= todayString) // Only show today and future reservations
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [reservations]);

  const reservationMap = useMemo(() => {
    const map = new Map<string, Reservation>();
    sortedReservations.forEach(res => map.set(res.date, res));
    return map;
  }, [sortedReservations]);

  // Scroll the list when calendar highlights a date
  useEffect(() => {
    if (highlightedDate && reservationRefs.current.has(highlightedDate)) {
      const element = reservationRefs.current.get(highlightedDate);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightedDate]);

  const handleDateSelect = (date: Date) => {
    const dateString = date.toISOString().split('T')[0];
    if (reservationMap.has(dateString)) {
      setHighlightedDate(dateString); // Highlight existing reservation
    } else {
      navigate(`/reserve?date=${dateString}`); // Jump to reserve page
    }
  };
  
  const handleCardClick = (reservation: Reservation) => {
    navigate(`/reserve?date=${reservation.date}&deskId=${reservation.deskId}`);
  }

  if (loading) return <h1>Loading your overview...</h1>;
  if (!user) return <h1>Please log in.</h1>;

  const getDeskInfo = (deskId: string) => desks.find(d => d.id === deskId) || { name: 'N/A', room: 'N/A' };

  return (
    <div className="personal-page-layout">
      <h1>My Reservations Overview</h1>
      <p>Hello, {user.name}! Click a date to book, or a card to view your reservation on the map.</p>
      
      <div className="overview-container">
        <div className="overview-calendar-pane">
          <OverviewCalendar 
            reservations={sortedReservations}
            highlightedDate={highlightedDate}
            onDateHighlight={setHighlightedDate}
            onDateSelect={handleDateSelect}
          />
        </div>

        <div className="overview-list-pane">
          <h2>Upcoming Bookings</h2>
          <div className="overview-list-scroll">
            {sortedReservations.length === 0 ? (
              <div className="no-reservations-message">
                <span>📅</span>
                <p>No upcoming reservations.</p>
                <small>Click a date on the calendar to book a desk.</small>
              </div>
            ) : (
              sortedReservations.map(reservation => {
                const desk = getDeskInfo(reservation.deskId);
                const isAuto = reservation.id.startsWith('res-auto-');
                return (
                <div
                  key={reservation.id}
                  ref={(el) => { if (el) reservationRefs.current.set(reservation.date, el); }}
                  className={`reservation-card-overview ${highlightedDate === reservation.date ? 'highlighted' : ''}`}
                  onMouseEnter={() => setHighlightedDate(reservation.date)}
                >
                    <div className="card-header-overview">
                        <h3>{new Date(reservation.date + 'T00:00:00').toDateString()}</h3>
                        <span className={`badge ${isAuto ? 'auto' : 'manual'}`}>
                            {isAuto ? 'Auto' : 'Manual'}
                        </span>
                        <button onClick={() => handleCardClick(reservation)} className="view-map-button">
                            View
                        </button>
                    </div>
                    <div className="card-body-overview">
                        <p><CalendarIcon /> Room: <strong>{desk.room}</strong>&nbsp;<DeskIcon /> Desk: <strong>{desk.name}</strong></p>
                    </div> 
                </div>
              )})
            )}
          </div>
        </div>
      </div>
    </div>
  );
};